import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Images, Plus, Save, Trash2, Eye, EyeOff, ChevronUp, ChevronDown,
    AlertCircle, Loader2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
    fetchAdminHeroRail, createHeroRailItem, updateHeroRailItem,
    deleteHeroRailItem, reorderHeroRail,
} from "@/lib/adminApi";

/**
 * AdminHeroRail — manage the curved image rail behind the homepage hero.
 *
 * No hard-coded limit. Admins can add as many images as they wish; the frontend
 * rail auto-adapts to whatever count is currently visible.
 */

const emptyDraft = () => ({ id: null, image_url: "", alt: "", visible: true });

export default function AdminHeroRail() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState(null);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchAdminHeroRail();
            setItems(Array.isArray(data.items) ? data.items : []);
        } catch (_e) {
            toast.error("Could not load hero rail images");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => setDraft(emptyDraft());
    const openEdit = (item) => setEditing({ ...item });

    const submitDraft = async () => {
        if (!draft?.image_url?.trim()) {
            toast.error("Image URL is required");
            return;
        }
        setSaving(true);
        try {
            await createHeroRailItem({
                image_url: draft.image_url.trim(),
                alt: (draft.alt || "").trim(),
                visible: draft.visible,
            });
            setDraft(null);
            await load();
            toast.success("Image added to hero rail");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error("Could not add image", { description: Array.isArray(detail) ? detail[0]?.msg : detail });
        } finally {
            setSaving(false);
        }
    };

    const submitEdit = async () => {
        if (!editing?.image_url?.trim()) {
            toast.error("Image URL is required");
            return;
        }
        setSaving(true);
        try {
            await updateHeroRailItem(editing.id, {
                image_url: editing.image_url.trim(),
                alt: (editing.alt || "").trim(),
                visible: editing.visible,
            });
            setEditing(null);
            await load();
            toast.success("Saved");
        } catch (_err) {
            toast.error("Could not save changes");
        } finally {
            setSaving(false);
        }
    };

    const toggleVisible = async (item) => {
        try {
            await updateHeroRailItem(item.id, { visible: !item.visible });
            await load();
        } catch (_e) {
            toast.error("Could not toggle visibility");
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Remove this image from the hero rail? "${item.alt || item.image_url}"`)) return;
        try {
            await deleteHeroRailItem(item.id);
            await load();
            toast.success("Image removed");
        } catch (_e) {
            toast.error("Could not remove image");
        }
    };

    const move = async (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= items.length) return;
        const next = [...items];
        [next[idx], next[target]] = [next[target], next[idx]];
        setItems(next); // optimistic
        try {
            await reorderHeroRail(next.map((i) => i.id));
        } catch (_e) {
            toast.error("Could not reorder — reverting");
            await load();
        }
    };

    return (
        <div className="px-5 py-8 sm:px-8 sm:py-10 lg:px-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="pg-eyebrow inline-flex items-center gap-2">
                        <Images className="h-3.5 w-3.5" /> Studio CMS · Hero Rail
                    </span>
                    <h1 className="font-display mt-4 text-3xl sm:text-4xl" style={{ color: "var(--pg-fg)" }} data-testid="admin-hero-rail-headline">
                        Hero Image Rail
                    </h1>
                    <p className="mt-2 max-w-xl text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                        Curved panoramic gallery running behind the homepage hero. Add any number of images —
                        the animation adapts automatically. Speed is controlled in{" "}
                        <a href="/admin/content" className="pg-link-underline" style={{ color: "var(--pg-accent)" }}>Content → Branding</a>.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={load}
                        className="pg-btn-ghost pg-app-tap"
                        data-testid="admin-hero-rail-refresh"
                    >
                        <RefreshCw className="h-4 w-4" /> Refresh
                    </button>
                    <button
                        type="button"
                        onClick={openCreate}
                        className="pg-btn-primary pg-app-tap"
                        data-testid="admin-hero-rail-new"
                    >
                        <Plus className="h-4 w-4" /> Add Image
                    </button>
                </div>
            </div>

            {/* Empty / loading / list */}
            <div className="mt-8">
                {loading ? (
                    <div className="flex items-center justify-center rounded-2xl border py-16 text-sm"
                         style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg-muted)" }}>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading images…
                    </div>
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border py-20 text-center" style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}>
                        <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full" style={{ background: "color-mix(in srgb, var(--pg-accent) 12%, transparent)" }}>
                            <Images className="h-6 w-6" style={{ color: "var(--pg-accent)" }} />
                        </div>
                        <div className="font-display text-lg" style={{ color: "var(--pg-fg)" }}>No hero images yet</div>
                        <p className="mx-auto mt-2 max-w-md text-sm" style={{ color: "var(--pg-fg-muted)" }}>
                            Add your first image to bring the hero to life. The rail hides itself when the collection is empty.
                        </p>
                        <button type="button" onClick={openCreate} className="pg-btn-primary pg-app-tap mt-6" data-testid="admin-hero-rail-empty-cta">
                            <Plus className="h-4 w-4" /> Add First Image
                        </button>
                    </div>
                ) : (
                    <ul className="grid gap-3" data-testid="admin-hero-rail-list">
                        {items.map((item, idx) => (
                            <li
                                key={item.id}
                                className="flex items-center gap-4 rounded-2xl border p-3"
                                style={{
                                    borderColor: "var(--pg-border)",
                                    background: "var(--pg-surface)",
                                    opacity: item.visible ? 1 : 0.6,
                                }}
                                data-testid={`admin-hero-rail-item-${item.id}`}
                            >
                                {/* Thumbnail */}
                                <div className="h-20 w-24 flex-shrink-0 overflow-hidden rounded-lg border"
                                     style={{ borderColor: "var(--pg-border)", background: "var(--pg-bg)" }}>
                                    <img
                                        src={item.image_url}
                                        alt={item.alt || ""}
                                        className="h-full w-full object-cover"
                                        onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                                    />
                                </div>

                                {/* Meta */}
                                <div className="min-w-0 flex-1">
                                    <div className="truncate font-display text-sm" style={{ color: "var(--pg-fg)" }}>
                                        {item.alt || "Untitled image"}
                                    </div>
                                    <div className="mt-0.5 truncate font-mono text-[11px]" style={{ color: "var(--pg-fg-muted)" }}>
                                        {item.image_url}
                                    </div>
                                    <div className="mt-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]"
                                         style={{ color: "var(--pg-fg-muted)" }}>
                                        <span>Order · {idx + 1}</span>
                                        <span>·</span>
                                        <span style={{ color: item.visible ? "var(--pg-accent)" : "var(--pg-fg-muted)" }}>
                                            {item.visible ? "Visible" : "Hidden"}
                                        </span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-1">
                                    <IconBtn
                                        onClick={() => move(idx, -1)}
                                        title="Move up"
                                        disabled={idx === 0}
                                        testId={`admin-hero-rail-up-${item.id}`}
                                    ><ChevronUp className="h-4 w-4" /></IconBtn>
                                    <IconBtn
                                        onClick={() => move(idx, +1)}
                                        title="Move down"
                                        disabled={idx === items.length - 1}
                                        testId={`admin-hero-rail-down-${item.id}`}
                                    ><ChevronDown className="h-4 w-4" /></IconBtn>
                                    <IconBtn
                                        onClick={() => toggleVisible(item)}
                                        title={item.visible ? "Hide" : "Show"}
                                        testId={`admin-hero-rail-toggle-${item.id}`}
                                    >{item.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</IconBtn>
                                    <button
                                        type="button"
                                        onClick={() => openEdit(item)}
                                        className="pg-app-tap rounded-lg border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em]"
                                        style={{ borderColor: "var(--pg-border)", color: "var(--pg-fg)" }}
                                        data-testid={`admin-hero-rail-edit-${item.id}`}
                                    >
                                        Edit
                                    </button>
                                    <IconBtn
                                        onClick={() => remove(item)}
                                        title="Delete"
                                        variant="danger"
                                        testId={`admin-hero-rail-delete-${item.id}`}
                                    ><Trash2 className="h-4 w-4" /></IconBtn>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Create / Edit dialogs */}
            <AnimatePresence>
                {(draft || editing) && (
                    <Dialog
                        onClose={() => { setDraft(null); setEditing(null); }}
                        title={editing ? "Edit hero image" : "Add hero image"}
                    >
                        <ItemForm
                            value={editing || draft}
                            onChange={(next) => (editing ? setEditing(next) : setDraft(next))}
                        />
                        <div className="mt-6 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => { setDraft(null); setEditing(null); }}
                                className="pg-btn-ghost pg-app-tap"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={editing ? submitEdit : submitDraft}
                                disabled={saving}
                                className="pg-btn-primary pg-app-tap disabled:opacity-60"
                                data-testid="admin-hero-rail-submit"
                            >
                                <Save className="h-4 w-4" /> {saving ? "Saving…" : editing ? "Save Changes" : "Add Image"}
                            </button>
                        </div>
                    </Dialog>
                )}
            </AnimatePresence>
        </div>
    );
}

// ---------- Sub-components ----------

const IconBtn = ({ onClick, children, title, disabled = false, variant, testId }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        disabled={disabled}
        data-testid={testId}
        className="pg-app-tap inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        style={{
            borderColor: "var(--pg-border)",
            background: "transparent",
            color: variant === "danger" ? "#e5534b" : "var(--pg-fg-muted)",
        }}
    >
        {children}
    </button>
);

const Dialog = ({ children, onClose, title }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
        onClick={onClose}
    >
        <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 12, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-lg rounded-2xl border p-6"
            style={{ borderColor: "var(--pg-border)", background: "var(--pg-bg)" }}
        >
            <div className="flex items-center justify-between">
                <div>
                    <span className="pg-eyebrow">Hero Rail</span>
                    <h3 className="font-display mt-2 text-xl" style={{ color: "var(--pg-fg)" }}>{title}</h3>
                </div>
            </div>
            <div className="mt-5">{children}</div>
        </motion.div>
    </motion.div>
);

const ItemForm = ({ value, onChange }) => (
    <div className="space-y-4">
        <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                Image URL <span style={{ color: "var(--pg-accent)" }}>*</span>
            </label>
            <input
                type="url"
                value={value.image_url || ""}
                onChange={(e) => onChange({ ...value, image_url: e.target.value })}
                className="pg-input mt-2 w-full"
                placeholder="https://…/project.jpg"
                data-testid="admin-hero-rail-image-url"
                autoFocus
            />
            {value.image_url && (
                <div className="mt-3 flex h-32 items-center justify-center overflow-hidden rounded-xl border"
                     style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}>
                    <img
                        src={value.image_url}
                        alt="Preview"
                        className="h-full w-full object-cover"
                        onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                    />
                </div>
            )}
        </div>

        <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "var(--pg-fg-muted)" }}>
                Alt / description
            </label>
            <input
                type="text"
                value={value.alt || ""}
                onChange={(e) => onChange({ ...value, alt: e.target.value })}
                className="pg-input mt-2 w-full"
                placeholder="Aurelia Atelier — brand identity system"
                data-testid="admin-hero-rail-alt"
            />
        </div>

        <label className="flex items-center gap-3 text-sm" style={{ color: "var(--pg-fg)" }}>
            <input
                type="checkbox"
                checked={value.visible !== false}
                onChange={(e) => onChange({ ...value, visible: e.target.checked })}
                data-testid="admin-hero-rail-visible"
                className="h-4 w-4"
            />
            <span>Visible in the hero rail</span>
        </label>

        <div className="flex items-start gap-2 rounded-xl border p-3 text-[12px]"
             style={{ borderColor: "var(--pg-border)", background: "color-mix(in srgb, var(--pg-accent) 6%, transparent)", color: "var(--pg-fg-muted)" }}>
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" style={{ color: "var(--pg-accent)" }} />
            <span>Any hosted image works — direct URL, Unsplash, or your own uploads via the existing customer-assets endpoint.</span>
        </div>
    </div>
);
